/**
 * Organelle Renderer
 * Renders individual organelles based on PRD Section 3.2.3
 */

import Phaser from 'phaser';
import { SimplexNoise } from '@procedural/noise';
import { CELL_COLORS } from '@/utils/colors';
import { SeededRandom } from '@/utils/math';
import { OrganelleType } from '@/types';

export class OrganelleRenderer {
  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Render organelles of a given type
   */
  renderOrganelle(
    type: OrganelleType,
    cellRadius: number,
    density: number,
    noise: SimplexNoise,
    random: SeededRandom
  ): Phaser.GameObjects.Graphics[] {
    switch (type) {
      case OrganelleType.MITOCHONDRIA:
        return this.renderMitochondria(cellRadius, density, noise, random);
      case OrganelleType.RIBOSOME:
        return this.renderRibosomes(cellRadius, density, noise, random);
      case OrganelleType.ENDOPLASMIC_RETICULUM:
        return this.renderEndoplasmicReticulum(cellRadius, noise);
      case OrganelleType.CHLOROPLAST:
        return this.renderChloroplasts(cellRadius, density, noise, random);
      case OrganelleType.VACUOLE:
        return this.renderVacuoles(cellRadius, density, noise, random);
      case OrganelleType.CENTRAL_VACUOLE:
        return this.renderCentralVacuole(cellRadius, noise);
      case OrganelleType.CILIA:
        return this.renderCilia(cellRadius, noise);
      case OrganelleType.NUCLEOID:
        return this.renderNucleoid(cellRadius, noise);
      case OrganelleType.CONTRACTILE_VACUOLE:
        return this.renderContractileVacuole(cellRadius, noise, random);
      case OrganelleType.FOOD_VACUOLE:
        return this.renderFoodVacuoles(cellRadius, density, noise, random);
      case OrganelleType.ORAL_GROOVE:
        return this.renderOralGroove(cellRadius, noise);
      default:
        return [];
    }
  }

  /**
   * Render mitochondria - elongated ovals with cristae folds
   */
  private renderMitochondria(
    cellRadius: number,
    density: number,
    noise: SimplexNoise,
    random: SeededRandom
  ): Phaser.GameObjects.Graphics[] {
    const mitochondria: Phaser.GameObjects.Graphics[] = [];
    const count = Math.floor(5 + density * 15);

    for (let i = 0; i < count; i++) {
      const graphics = this.scene.add.graphics();

      // Random position within cell (avoiding center where nucleus is)
      const angle = random.next() * Math.PI * 2;
      const dist = cellRadius * (0.3 + random.next() * 0.5);
      const x = Math.cos(angle) * dist;
      const y = Math.sin(angle) * dist;

      // Size variation
      const length = 8 + random.next() * 12;
      const width = length * 0.4;

      // Random rotation
      const rotation = random.next() * Math.PI;

      this.drawMitochondrion(graphics, x, y, length, width, rotation, noise, i);
      mitochondria.push(graphics);
    }

    return mitochondria;
  }

  /**
   * Draw a single mitochondrion
   */
  private drawMitochondrion(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    length: number,
    width: number,
    rotation: number,
    noise: SimplexNoise,
    seed: number
  ): void {
    // Outer membrane
    graphics.fillStyle(CELL_COLORS.mitochondria, 0.7);

    // Draw elongated oval shape (calculate points manually since Phaser doesn't have transform)

    const points = this.generateElongatedOval(x, y, length, width, rotation, 16, noise, seed);

    graphics.beginPath();
    graphics.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      graphics.lineTo(points[i].x, points[i].y);
    }
    graphics.closePath();
    graphics.fillPath();

    // Inner cristae folds
    graphics.lineStyle(1, 0xFFBA08, 0.5);
    const foldsCount = Math.floor(length / 4);

    for (let i = 0; i < foldsCount; i++) {
      const t = (i + 1) / (foldsCount + 1);
      const foldX = x + Math.cos(rotation) * (t - 0.5) * length * 1.5;
      const foldY = y + Math.sin(rotation) * (t - 0.5) * length * 1.5;

      // Perpendicular fold
      const perpAngle = rotation + Math.PI / 2;
      const foldHeight = width * 0.5 * (1 + noise.noise2D(seed + i, 0) * 0.2);

      graphics.beginPath();
      graphics.moveTo(
        foldX - Math.cos(perpAngle) * foldHeight,
        foldY - Math.sin(perpAngle) * foldHeight
      );

      // Wavy fold
      const segments = 4;
      for (let j = 0; j <= segments; j++) {
        const st = j / segments;
        const waveX = foldX + Math.cos(perpAngle) * foldHeight * (st * 2 - 1);
        const waveY = foldY + Math.sin(perpAngle) * foldHeight * (st * 2 - 1);
        const offsetX = Math.cos(rotation) * noise.noise2D(seed + i, j) * 2;
        const offsetY = Math.sin(rotation) * noise.noise2D(seed + i, j) * 2;
        graphics.lineTo(waveX + offsetX, waveY + offsetY);
      }

      graphics.strokePath();
    }

    // Outer membrane stroke
    graphics.lineStyle(1, CELL_COLORS.mitochondria, 0.9);
    graphics.beginPath();
    graphics.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      graphics.lineTo(points[i].x, points[i].y);
    }
    graphics.closePath();
    graphics.strokePath();
  }

  /**
   * Generate elongated oval points
   */
  private generateElongatedOval(
    cx: number,
    cy: number,
    length: number,
    width: number,
    rotation: number,
    pointCount: number,
    noise: SimplexNoise,
    seed: number
  ): { x: number; y: number }[] {
    const points: { x: number; y: number }[] = [];

    for (let i = 0; i < pointCount; i++) {
      const angle = (i / pointCount) * Math.PI * 2;

      // Base ellipse point
      let px = Math.cos(angle) * length / 2;
      let py = Math.sin(angle) * width / 2;

      // Add slight irregularity
      const noiseVal = noise.noise2D(seed + i * 0.1, 0) * 0.1;
      px *= 1 + noiseVal;
      py *= 1 + noiseVal;

      // Rotate
      const rotatedX = px * Math.cos(rotation) - py * Math.sin(rotation);
      const rotatedY = px * Math.sin(rotation) + py * Math.cos(rotation);

      points.push({
        x: cx + rotatedX,
        y: cy + rotatedY,
      });
    }

    return points;
  }

  /**
   * Render ribosomes - small dots scattered throughout
   */
  private renderRibosomes(
    cellRadius: number,
    density: number,
    noise: SimplexNoise,
    random: SeededRandom
  ): Phaser.GameObjects.Graphics[] {
    const graphics = this.scene.add.graphics();
    const count = Math.floor(50 + density * 150);

    graphics.fillStyle(CELL_COLORS.ribosome, 0.6);

    for (let i = 0; i < count; i++) {
      // Distribute throughout cytoplasm
      const angle = random.next() * Math.PI * 2;
      const dist = cellRadius * (0.15 + random.next() * 0.7);
      const x = Math.cos(angle) * dist;
      const y = Math.sin(angle) * dist;

      const size = 1.5 + noise.noise2DNormalized(i, 0) * 1.5;
      graphics.fillCircle(x, y, size);
    }

    return [graphics];
  }

  /**
   * Render endoplasmic reticulum - network of tubular channels
   */
  private renderEndoplasmicReticulum(
    cellRadius: number,
    noise: SimplexNoise
  ): Phaser.GameObjects.Graphics[] {
    const graphics = this.scene.add.graphics();

    graphics.lineStyle(2, CELL_COLORS.endoplasmicReticulum, 0.4);

    // Draw network near nucleus
    const networkRadius = cellRadius * 0.4;
    const branchCount = 8;

    for (let i = 0; i < branchCount; i++) {
      const startAngle = (i / branchCount) * Math.PI * 2;
      const startX = Math.cos(startAngle) * networkRadius * 0.3;
      const startY = Math.sin(startAngle) * networkRadius * 0.3;

      graphics.beginPath();
      graphics.moveTo(startX, startY);

      // Draw wavy branch
      const segments = 8;
      for (let j = 1; j <= segments; j++) {
        const t = j / segments;
        const dist = networkRadius * 0.3 + t * networkRadius * 0.5;
        const angleOffset = noise.noise2D(i, j) * 0.3;
        const x = Math.cos(startAngle + angleOffset) * dist;
        const y = Math.sin(startAngle + angleOffset) * dist;
        graphics.lineTo(x, y);
      }

      graphics.strokePath();

      // Add some cross-connections
      if (i % 2 === 0) {
        const midX = Math.cos(startAngle) * networkRadius * 0.5;
        const midY = Math.sin(startAngle) * networkRadius * 0.5;
        const nextAngle = ((i + 1) / branchCount) * Math.PI * 2;
        const nextX = Math.cos(nextAngle) * networkRadius * 0.5;
        const nextY = Math.sin(nextAngle) * networkRadius * 0.5;

        graphics.beginPath();
        graphics.moveTo(midX, midY);
        graphics.lineTo(nextX, nextY);
        graphics.strokePath();
      }
    }

    return [graphics];
  }

  /**
   * Render chloroplasts - lens-shaped with thylakoid stacks
   */
  private renderChloroplasts(
    cellRadius: number,
    density: number,
    noise: SimplexNoise,
    random: SeededRandom
  ): Phaser.GameObjects.Graphics[] {
    const chloroplasts: Phaser.GameObjects.Graphics[] = [];
    const count = Math.floor(10 + density * 30);

    for (let i = 0; i < count; i++) {
      const graphics = this.scene.add.graphics();

      // Position throughout cell
      const angle = random.next() * Math.PI * 2;
      const dist = cellRadius * (0.2 + random.next() * 0.6);
      const x = Math.cos(angle) * dist;
      const y = Math.sin(angle) * dist;

      const size = 10 + random.next() * 8;
      const rotation = random.next() * Math.PI;

      this.drawChloroplast(graphics, x, y, size, rotation, noise, i);
      chloroplasts.push(graphics);
    }

    return chloroplasts;
  }

  /**
   * Draw a single chloroplast
   */
  private drawChloroplast(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    size: number,
    rotation: number,
    noise: SimplexNoise,
    seed: number
  ): void {
    // Outer envelope
    graphics.fillStyle(CELL_COLORS.chloroplast, 0.7);

    const points = this.generateElongatedOval(x, y, size * 1.5, size, rotation, 16, noise, seed);

    graphics.beginPath();
    graphics.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      graphics.lineTo(points[i].x, points[i].y);
    }
    graphics.closePath();
    graphics.fillPath();

    // Thylakoid stacks (grana)
    graphics.fillStyle(0x2F855A, 0.6);
    const stackCount = 4;

    for (let i = 0; i < stackCount; i++) {
      const t = (i + 0.5) / stackCount - 0.5;
      const stackX = x + Math.cos(rotation) * t * size;
      const stackY = y + Math.sin(rotation) * t * size;
      const stackWidth = size * 0.3;
      const stackHeight = size * 0.6;

      // Draw stack as small rectangle rotated
      const corners = [
        { x: -stackWidth / 2, y: -stackHeight / 2 },
        { x: stackWidth / 2, y: -stackHeight / 2 },
        { x: stackWidth / 2, y: stackHeight / 2 },
        { x: -stackWidth / 2, y: stackHeight / 2 },
      ];

      graphics.beginPath();
      const perpRotation = rotation + Math.PI / 2;
      for (let j = 0; j < corners.length; j++) {
        const rx = corners[j].x * Math.cos(perpRotation) - corners[j].y * Math.sin(perpRotation);
        const ry = corners[j].x * Math.sin(perpRotation) + corners[j].y * Math.cos(perpRotation);
        if (j === 0) {
          graphics.moveTo(stackX + rx, stackY + ry);
        } else {
          graphics.lineTo(stackX + rx, stackY + ry);
        }
      }
      graphics.closePath();
      graphics.fillPath();
    }

    // Outer stroke
    graphics.lineStyle(1, CELL_COLORS.chloroplast, 0.9);
    graphics.beginPath();
    graphics.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      graphics.lineTo(points[i].x, points[i].y);
    }
    graphics.closePath();
    graphics.strokePath();
  }

  /**
   * Render vacuoles - fluid-filled sacs
   */
  private renderVacuoles(
    cellRadius: number,
    density: number,
    noise: SimplexNoise,
    random: SeededRandom
  ): Phaser.GameObjects.Graphics[] {
    const vacuoles: Phaser.GameObjects.Graphics[] = [];
    const count = Math.floor(1 + density * 4);

    for (let i = 0; i < count; i++) {
      const graphics = this.scene.add.graphics();

      const angle = random.next() * Math.PI * 2;
      const dist = cellRadius * (0.3 + random.next() * 0.4);
      const x = Math.cos(angle) * dist;
      const y = Math.sin(angle) * dist;

      const size = 15 + random.next() * 25;

      // Irregular shape
      const points: { x: number; y: number }[] = [];
      const pointCount = 12;
      for (let j = 0; j < pointCount; j++) {
        const a = (j / pointCount) * Math.PI * 2;
        const r = size * (1 + noise.noise2D(i + j * 0.5, 0) * 0.3);
        points.push({
          x: x + Math.cos(a) * r,
          y: y + Math.sin(a) * r,
        });
      }

      // Fill
      graphics.fillStyle(CELL_COLORS.vacuole, 0.3);
      graphics.beginPath();
      graphics.moveTo(points[0].x, points[0].y);
      for (let j = 1; j < points.length; j++) {
        graphics.lineTo(points[j].x, points[j].y);
      }
      graphics.closePath();
      graphics.fillPath();

      // Stroke
      graphics.lineStyle(1, CELL_COLORS.vacuole, 0.5);
      graphics.beginPath();
      graphics.moveTo(points[0].x, points[0].y);
      for (let j = 1; j < points.length; j++) {
        graphics.lineTo(points[j].x, points[j].y);
      }
      graphics.closePath();
      graphics.strokePath();

      vacuoles.push(graphics);
    }

    return vacuoles;
  }

  /**
   * Render central vacuole for plant cells
   */
  private renderCentralVacuole(
    cellRadius: number,
    noise: SimplexNoise
  ): Phaser.GameObjects.Graphics[] {
    const graphics = this.scene.add.graphics();

    // Large central vacuole taking up most of cell
    const size = cellRadius * 0.6;

    const points: { x: number; y: number }[] = [];
    const pointCount = 24;
    for (let i = 0; i < pointCount; i++) {
      const angle = (i / pointCount) * Math.PI * 2;
      const r = size * (1 + noise.noise2D(i * 0.3, 0) * 0.15);
      points.push({
        x: Math.cos(angle) * r,
        y: Math.sin(angle) * r,
      });
    }

    // Fill with very light blue
    graphics.fillStyle(CELL_COLORS.vacuole, 0.2);
    graphics.beginPath();
    graphics.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      graphics.lineTo(points[i].x, points[i].y);
    }
    graphics.closePath();
    graphics.fillPath();

    // Tonoplast (vacuole membrane)
    graphics.lineStyle(2, CELL_COLORS.vacuole, 0.4);
    graphics.beginPath();
    graphics.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      graphics.lineTo(points[i].x, points[i].y);
    }
    graphics.closePath();
    graphics.strokePath();

    return [graphics];
  }

  /**
   * Render cilia around cell edge
   */
  private renderCilia(
    cellRadius: number,
    noise: SimplexNoise
  ): Phaser.GameObjects.Graphics[] {
    const graphics = this.scene.add.graphics();

    const ciliaCount = 60;
    const ciliaLength = 8;

    graphics.lineStyle(1, CELL_COLORS.cilia, 0.6);

    for (let i = 0; i < ciliaCount; i++) {
      const angle = (i / ciliaCount) * Math.PI * 2;
      const startX = Math.cos(angle) * cellRadius;
      const startY = Math.sin(angle) * cellRadius;

      // Slight wave to cilia
      const wave = noise.noise2D(i * 0.5, 0) * 0.3;
      const endAngle = angle + wave;
      const endX = Math.cos(endAngle) * (cellRadius + ciliaLength);
      const endY = Math.sin(endAngle) * (cellRadius + ciliaLength);

      graphics.beginPath();
      graphics.moveTo(startX, startY);
      graphics.lineTo(endX, endY);
      graphics.strokePath();
    }

    return [graphics];
  }

  /**
   * Render nucleoid (DNA region for prokaryotes)
   */
  private renderNucleoid(
    cellRadius: number,
    noise: SimplexNoise
  ): Phaser.GameObjects.Graphics[] {
    const graphics = this.scene.add.graphics();

    // Irregular DNA region in center
    const size = cellRadius * 0.3;

    graphics.fillStyle(CELL_COLORS.nucleoid, 0.4);

    const points: { x: number; y: number }[] = [];
    const pointCount = 12;
    for (let i = 0; i < pointCount; i++) {
      const angle = (i / pointCount) * Math.PI * 2;
      const r = size * (1 + noise.noise2D(i * 0.5, 0) * 0.4);
      points.push({
        x: Math.cos(angle) * r,
        y: Math.sin(angle) * r,
      });
    }

    graphics.beginPath();
    graphics.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      graphics.lineTo(points[i].x, points[i].y);
    }
    graphics.closePath();
    graphics.fillPath();

    // DNA strands
    graphics.lineStyle(1, CELL_COLORS.nucleoid, 0.6);
    for (let i = 0; i < 5; i++) {
      const startAngle = noise.noise2D(i, 10) * Math.PI * 2;
      const startR = size * 0.2;
      graphics.beginPath();
      graphics.moveTo(Math.cos(startAngle) * startR, Math.sin(startAngle) * startR);

      for (let j = 1; j <= 6; j++) {
        const t = j / 6;
        const a = startAngle + t * Math.PI + noise.noise2D(i, j) * 0.5;
        const r = startR + t * size * 0.6;
        graphics.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      }
      graphics.strokePath();
    }

    return [graphics];
  }

  /**
   * Render contractile vacuole (for paramecium)
   */
  private renderContractileVacuole(
    cellRadius: number,
    _noise: SimplexNoise,
    random: SeededRandom
  ): Phaser.GameObjects.Graphics[] {
    const graphics = this.scene.add.graphics();

    // Two contractile vacuoles at ends
    const positions = [
      { x: -cellRadius * 0.6, y: 0 },
      { x: cellRadius * 0.6, y: 0 },
    ];

    for (const pos of positions) {
      const size = 8 + random.next() * 4;

      // Star-like radiating canals
      graphics.lineStyle(1, 0x63B3ED, 0.5);
      const rayCount = 6;
      for (let i = 0; i < rayCount; i++) {
        const angle = (i / rayCount) * Math.PI * 2;
        const rayLength = size * 1.5;
        graphics.beginPath();
        graphics.moveTo(pos.x, pos.y);
        graphics.lineTo(
          pos.x + Math.cos(angle) * rayLength,
          pos.y + Math.sin(angle) * rayLength
        );
        graphics.strokePath();
      }

      // Central vacuole
      graphics.fillStyle(CELL_COLORS.vacuole, 0.4);
      graphics.fillCircle(pos.x, pos.y, size);
      graphics.lineStyle(1, CELL_COLORS.vacuole, 0.6);
      graphics.strokeCircle(pos.x, pos.y, size);
    }

    return [graphics];
  }

  /**
   * Render food vacuoles
   */
  private renderFoodVacuoles(
    cellRadius: number,
    density: number,
    _noise: SimplexNoise,
    random: SeededRandom
  ): Phaser.GameObjects.Graphics[] {
    const vacuoles: Phaser.GameObjects.Graphics[] = [];
    const count = Math.floor(1 + density * 3);

    for (let i = 0; i < count; i++) {
      const graphics = this.scene.add.graphics();

      const angle = random.next() * Math.PI * 2;
      const dist = cellRadius * (0.3 + random.next() * 0.3);
      const x = Math.cos(angle) * dist;
      const y = Math.sin(angle) * dist;
      const size = 6 + random.next() * 8;

      // Food vacuole with darker interior
      graphics.fillStyle(0x744210, 0.4);
      graphics.fillCircle(x, y, size);
      graphics.lineStyle(1, 0x975A16, 0.6);
      graphics.strokeCircle(x, y, size);

      // Food particle inside
      graphics.fillStyle(0x553C9A, 0.5);
      graphics.fillCircle(x, y, size * 0.5);

      vacuoles.push(graphics);
    }

    return vacuoles;
  }

  /**
   * Render oral groove (for paramecium)
   */
  private renderOralGroove(
    cellRadius: number,
    noise: SimplexNoise
  ): Phaser.GameObjects.Graphics[] {
    const graphics = this.scene.add.graphics();

    // Funnel-shaped groove on one side
    const grooveX = -cellRadius * 0.3;
    const grooveY = 0;

    graphics.lineStyle(2, 0x68D391, 0.5);

    // Draw curved groove
    graphics.beginPath();
    graphics.moveTo(grooveX - 15, grooveY - 20);

    // Curve into cell
    for (let i = 0; i <= 10; i++) {
      const t = i / 10;
      const x = grooveX - 15 + t * 20;
      const y = grooveY - 20 + t * 40;
      const curveX = x + Math.sin(t * Math.PI) * 10;
      graphics.lineTo(curveX, y);
    }

    graphics.strokePath();

    // Cilia inside groove
    graphics.lineStyle(1, CELL_COLORS.cilia, 0.4);
    for (let i = 0; i < 8; i++) {
      const t = i / 8;
      const startX = grooveX - 15 + t * 20 + Math.sin(t * Math.PI) * 10;
      const startY = grooveY - 20 + t * 40;
      const ciliaLen = 5;

      graphics.beginPath();
      graphics.moveTo(startX, startY);
      graphics.lineTo(startX - ciliaLen, startY + noise.noise2D(i, 0) * 3);
      graphics.strokePath();
    }

    return [graphics];
  }
}
